import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';

export const URINE_PROTEIN = ['Nil', 'Trace', '1+', '2+', '3+'] as const;

export interface GrowthReference {
  months: number[];
  median: number[];
  minus2: number[];
  plus2: number[];
}
export interface ScheduleDose {
  vaccine: string;
  dueLabel: string;
  dueMonths: number;
  optional?: boolean;
}
export interface MchConfig {
  approved: boolean;
  approvedAt: string | null;
  approvedBy: string | null;
  source: string;
  antenatal: {
    gestationDays: number;
    bpFlag: { systolic: number; diastolic: number };
    proteinFlagFrom: string;
  };
  children: { maxAgeYears: number };
  growthReference: {
    label: string;
    boys: GrowthReference | null;
    girls: GrowthReference | null;
  };
  vaccineSchedule: ScheduleDose[];
}
export interface PatientRef {
  id: string;
  fullName: string;
}
export interface AntenatalVisit {
  id: string;
  visitDate: string;
  weeks: number;
  weightKg: number;
  systolic: number;
  diastolic: number;
  urineProtein: string;
  fundalHeightCm: number | null;
  fetalHeartRate: number | null;
  note: string | null;
  flag: boolean;
}
export interface Pregnancy {
  id: string;
  patient: PatientRef;
  lmp: string;
  edd: string;
  gestation: { weeks: number; days: number };
  gravidaPara: string;
  risks: string[];
  visits: AntenatalVisit[];
  preEclampsiaFlag: boolean;
}
export interface Child {
  patient: PatientRef & { sex: 'male' | 'female' | null };
  dateOfBirth: string;
  ageMonths: number;
  growth: {
    id: string;
    measuredAt: string;
    ageMonths: number;
    weightKg: number;
    lengthCm: number;
  }[];
  schedule: (ScheduleDose & {
    dueAt: string;
    given: { givenAt: string; where: string; batch: string | null } | null;
    overdue: boolean;
  })[];
  givenHere: { vaccine: string; dose: string | null; at: string }[];
}

function useMchQuery<T>(key: string, path: string, enabled = true) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['mch', key, selected?.id ?? 'all'],
    enabled,
    queryFn: ({ signal }) => api.get<T>(path, { signal }).then((r) => r.data),
  });
}

export const useMchConfig = () =>
  useMchQuery<MchConfig>('config', '/mch/config');
export const usePregnancies = () =>
  useMchQuery<{ config: MchConfig; rows: Pregnancy[] }>(
    'pregnancies',
    '/mch/pregnancies',
  );
export const useChildren = (enabled = true) =>
  useMchQuery<{ config: MchConfig; rows: Child[] }>(
    'children',
    '/mch/children',
    enabled,
  );

export function useMchAction<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['mch'] }),
  });
}

export const mchApi = {
  start: (v: {
    patientId: string;
    lmp: string;
    gravidaPara: string;
    risks: string[];
  }) => api.post('/mch/pregnancies', v),
  visit: ({ id, ...v }: { id: string } & Record<string, unknown>) =>
    api.post(`/mch/pregnancies/${id}/visits`, v),
  close: ({
    id,
    ...v
  }: {
    id: string;
    outcome: string;
    at: string;
    note?: string;
  }) => api.post(`/mch/pregnancies/${id}/outcome`, v),
  growth: ({
    patientId,
    ...v
  }: {
    patientId: string;
    measuredAt: string;
    weightKg: number;
    lengthCm: number;
  }) => api.post(`/mch/children/${patientId}/growth`, v),
  vaccine: ({
    patientId,
    ...v
  }: {
    patientId: string;
    vaccine: string;
    givenAt: string;
    where: string;
    batch?: string;
  }) => api.post(`/mch/children/${patientId}/vaccines`, v),
  approve: (v: { note: string }) => api.post('/mch/config/approve', v),
};
